import * as React from "react";
import { cn } from "@/lib/utils";
import type { OrderDecoprofStatus } from "@/lib/ordersDecoprof";

/**
 * 3D-pill badge — gebruikt voor zowel status als type-order.
 * Status-kleuren: blauw / groen / paars.
 * Type-kleuren: oranje / cyaan / amber / roze (botst niet met status).
 */
const STATUS_COLORS: Record<OrderDecoprofStatus, { bg: string; ring: string }> = {
  Nieuw:       { bg: "bg-blue-500",    ring: "ring-blue-300/50" },
  Uitgeprint:  { bg: "bg-emerald-500", ring: "ring-emerald-300/50" },
  Aangemaakt:  { bg: "bg-violet-500",  ring: "ring-violet-300/50" },
};

interface Props {
  status: OrderDecoprofStatus;
  className?: string;
  onClick?: () => void;
  interactive?: boolean;
  title?: string;
}

const baseClasses = (bg: string, ring: string) =>
  cn(
    "relative inline-flex items-center justify-center text-center",
    // R34: smaller min-w op mobile zodat status + type badges naast elkaar
    // passen op een 375px viewport.
    "min-w-[5.5rem] sm:min-w-[8rem] h-7 px-2 sm:px-3 text-[11px] sm:text-xs font-semibold text-white rounded-full",
    "shadow-[0_2px_4px_rgba(0,0,0,0.15),inset_0_1px_0_rgba(255,255,255,0.35),inset_0_-1px_0_rgba(0,0,0,0.15)]",
    "ring-1",
    bg,
    ring,
  );

export const BadgeStatus3D: React.FC<Props> = ({ status, className, onClick, interactive, title }) => {
  const c = STATUS_COLORS[status] ?? STATUS_COLORS.Nieuw;
  const isInteractive = interactive && !!onClick;

  const handleClick = (e: React.MouseEvent) => {
    if (!isInteractive) return;
    e.stopPropagation();
    onClick?.();
  };

  const Comp: any = isInteractive ? "button" : "span";

  return (
    <Comp
      type={isInteractive ? "button" : undefined}
      onClick={isInteractive ? handleClick : undefined}
      title={title}
      aria-label={title}
      className={cn(
        baseClasses(c.bg, c.ring),
        isInteractive && "cursor-pointer transition-transform duration-150 hover:scale-105 hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-violet-400",
        className,
      )}
    >
      <span
        aria-hidden
        className="absolute inset-x-1 top-0.5 h-1/2 rounded-full bg-white/20 blur-[1px] pointer-events-none"
      />
      <span className="relative">{status}</span>
    </Comp>
  );
};

// ----- Type-order badge (zelfde 3D-recipe, andere kleurenmap) ----------------

import type { OrderDecoprofType } from "@/lib/ordersDecoprof";

const TYPE_COLORS: Record<OrderDecoprofType, { bg: string; ring: string }> = {
  Verfbestelling:      { bg: "bg-orange-500", ring: "ring-orange-300/50" },
  Behangorder:         { bg: "bg-teal-500",   ring: "ring-teal-300/50" },
  Spuitbusbestelling:  { bg: "bg-amber-500",  ring: "ring-amber-300/50" },
  Overige:             { bg: "bg-pink-500",   ring: "ring-pink-300/50" },
};

export const BadgeType3D: React.FC<{ type: OrderDecoprofType; className?: string }> = ({ type, className }) => {
  const c = TYPE_COLORS[type] ?? TYPE_COLORS.Overige;
  return (
    <span className={cn(baseClasses(c.bg, c.ring), className)}>
      <span
        aria-hidden
        className="absolute inset-x-1 top-0.5 h-1/2 rounded-full bg-white/20 blur-[1px] pointer-events-none"
      />
      <span className="relative">{type}</span>
    </span>
  );
};

export default BadgeStatus3D;
